/**
 * C-16 CSV投入ウィザード（FR-10〜12）＝**差分を見せてから適用する**2段。
 *
 * ★ この画面の存在理由＝マスタの一括更新は「入れてみて違ったら戻す」が効かない
 *   （既存の単価を上書きした後、元の値はもう分からない）。だから先に差分を見せる。
 * ★ **エラー行が1件でもあれば適用しない**＝半端に入ったマスタは戻せない。
 */

import { Badge, Card, ErrorNote, formatDateTime, PageHeader, Table, Td } from "@kenzai/ui";
import { console_, dryRunImport, type ImportBatch } from "@kenzai/api-client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { hintStyle } from "./formStyles";
import { Link, useSearchParams } from "react-router-dom";

const KINDS = [
  { value: "products", label: "商品", columns: "商品番号 / 商品名 / 規格 / 単位 / 定価 / カテゴリ / 発注点" },
  { value: "customers", label: "取引先", columns: "得意先番号 / 得意先名 / 担当者 / 掛率 / アプリ発注 / 備考" },
  { value: "sites", label: "現場", columns: "得意先番号 / 現場名 / 住所" },
  { value: "prices", label: "個別単価", columns: "得意先番号 / 商品番号 / 単価" },
];

export function Imports() {
  const [params, setParams] = useSearchParams();
  const kind = params.get("kind") ?? "products";
  const qc = useQueryClient();
  const [batch, setBatch] = useState<ImportBatch | null>(null);
  const [error, setError] = useState<unknown>(null);

  const history = useQuery({ queryKey: ["imports"], queryFn: () => console_.imports() });

  const upload = useMutation({
    mutationFn: (file: File) => dryRunImport(kind, file),
    onSuccess: (b) => {
      setError(null);
      setBatch(b);
    },
    onError: (e) => {
      setBatch(null);
      setError(e);
    },
  });

  const apply = useMutation({
    mutationFn: (id: number) => console_.applyImport(id),
    onSuccess: (b) => {
      setError(null);
      setBatch(b);
      // 適用したらマスタの一覧を作り直す（見ている画面が古いままにならない）
      qc.invalidateQueries({ queryKey: ["products"] });
      qc.invalidateQueries({ queryKey: ["customers"] });
      qc.invalidateQueries({ queryKey: ["imports"] });
      qc.invalidateQueries({ queryKey: ["masters-meta"] });
    },
    onError: setError,
  });

  const spec = KINDS.find((k) => k.value === kind)!;
  const counts = batch?.summary?.counts as Record<string, number> | undefined;
  const rows = (batch?.summary?.rows ?? []) as {
    line_no: number;
    key: string;
    action: string;
    errors: string[];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- 行の形は kind ごとに違う
    values: any;
  }[];
  const hasErrors = (counts?.error ?? 0) > 0;

  return (
    <>
      <PageHeader title="CSV で取り込む" subtitle="内容を確認してから反映します。" />
      <ErrorNote error={error} />

      <Card style={{ marginBottom: "var(--kz-space-4)" }}>
        {/* 種別の切り替え＝セグメントタブ（M1.7・S3）。`.kz-tabs` の器と `.kz-tab` は1組 */}
        <div style={{ display: "flex", marginBottom: "var(--kz-space-4)" }}>
          <div className="kz-tabs">
          {KINDS.map((k) => (
            <button
              key={k.value}
              type="button"
              data-testid={`kind-${k.value}`}
              onClick={() => {
                setParams({ kind: k.value });
                setBatch(null);
                setError(null);
              }}
              aria-pressed={kind === k.value}
              className={kind === k.value ? "kz-tab is-selected" : "kz-tab"}
            >
              {k.label}
            </button>
          ))}
          </div>
        </div>

        <p style={{ fontSize: "var(--kz-font-size-sm)", color: "var(--kz-color-ink-muted)", marginTop: 0 }}>
          列：{spec.columns}
          <br />
          {/* 実装が受ける形をそのまま書く＝現場の Excel は CP932 で保存される */}
          文字コードは UTF-8 か Shift_JIS（Excel の既定）に対応しています。
          <strong>CSV に無い列は変更しません。</strong>
        </p>

        <input
          type="file"
          accept=".csv,text/csv"
          data-testid="csv-file"
          onChange={(e) => e.target.files?.[0] && upload.mutate(e.target.files[0])}
          style={{ font: "inherit" }}
        />
        {upload.isPending && <p>読み込み中…</p>}
      </Card>

      {batch && counts && (
        <Card style={{ marginBottom: "var(--kz-space-4)" }} data-testid="preview">
          <h2 style={{ margin: "0 0 var(--kz-space-3)", fontSize: "var(--kz-font-size-md)" }}>
            {batch.status === "applied" ? "取り込みました" : "この内容で反映されます"}
          </h2>
          <div style={{ display: "flex", gap: "var(--kz-space-3)", marginBottom: "var(--kz-space-4)" }}>
            <Badge tone="ok">新規 {counts.create}</Badge>
            <Badge tone="primary">更新 {counts.update}</Badge>
            <Badge>変更なし {counts.unchanged}</Badge>
            {hasErrors && <Badge tone="danger">エラー {counts.error}</Badge>}
          </div>

          {/* D-75（T-D55）＝商品を取り込むと自動で取引先アプリに掲載される。
              取り込んだ人が次にどこを見ればよいかを、この場で伝える（B1 の再発防止）。 */}
          {batch.status === "applied" && batch.kind === "products" && counts.create > 0 && (
            <p data-testid="applied-listing-note" style={{ ...hintStyle, marginBottom: "var(--kz-space-4)" }}>
              取り込んだ商品は<strong>自動で取引先アプリに掲載されます</strong>。掲載を外す・並び順や数量の刻みを変えるのは{" "}
              <Link to="/masters/catalogs" style={{ color: "var(--kz-color-primary)" }}>
                掲載管理
              </Link>
              から行えます。
            </p>
          )}

          {hasErrors && (
            <p
              style={{
                background: "var(--kz-status-danger-weak)",
                color: "var(--kz-status-danger)",
                padding: "var(--kz-space-3)",
                borderRadius: "var(--kz-radius-sm)",
                fontSize: "var(--kz-font-size-sm)",
              }}
            >
              {/* 全部か無か＝半端に入ったマスタは戻せない */}
              エラーのある行があるため、このファイルは取り込めません。CSV を直してからもう一度お試しください。
            </p>
          )}

          <div style={{ maxHeight: 360, overflowY: "auto" }}>
            <Table head={["行", "対象", "変更", "内容"]}>
              {rows.slice(0, 200).map((r) => (
                <tr key={r.line_no} data-testid={`import-row-${r.line_no}`}>
                  <Td>
                    <span className="kz-num">{r.line_no}</span>
                  </Td>
                  <Td>{r.key}</Td>
                  <Td>
                    {r.action === "create" ? (
                      <Badge tone="ok">新規</Badge>
                    ) : r.action === "update" ? (
                      <Badge tone="primary">更新</Badge>
                    ) : r.action === "error" ? (
                      <Badge tone="danger">エラー</Badge>
                    ) : (
                      <Badge>変更なし</Badge>
                    )}
                  </Td>
                  <Td style={{ fontSize: "var(--kz-font-size-sm)" }}>
                    {r.errors.length > 0 ? (
                      <span style={{ color: "var(--kz-status-danger)" }}>{r.errors.join(" / ")}</span>
                    ) : (
                      <span style={{ color: "var(--kz-color-ink-muted)" }}>
                        {Object.entries(r.values)
                          .filter(([k]) => !k.startsWith("_"))
                          .map(([k, v]) => `${k}=${v ?? ""}`)
                          .join(" / ")}
                      </span>
                    )}
                  </Td>
                </tr>
              ))}
            </Table>
            {rows.length > 200 && (
              <p style={{ fontSize: "var(--kz-font-size-sm)", color: "var(--kz-color-ink-muted)" }}>
                {/* 黙って切らない＝「200 件しか入らない」と誤解させない */}
                先頭 200 行のみ表示しています（取り込みは全 {rows.length} 行が対象です）。
              </p>
            )}
          </div>

          {batch.status === "dry_run" && (
            <button
              type="button"
              disabled={hasErrors || apply.isPending}
              onClick={() => apply.mutate(batch.id)}
              data-testid="apply-import"
              style={{
                marginTop: "var(--kz-space-4)",
                minHeight: 44,
                padding: "0 var(--kz-space-5)",
                border: "none",
                borderRadius: "var(--kz-radius-sm)",
                background: hasErrors ? "var(--kz-color-surface-sunken)" : "var(--kz-color-primary)",
                color: hasErrors ? "var(--kz-color-ink-muted)" : "var(--kz-color-ink-inverse)",
                font: "inherit",
                fontWeight: 700,
                cursor: hasErrors ? "not-allowed" : "pointer",
              }}
            >
              {apply.isPending ? "反映中…" : "この内容で反映する"}
            </button>
          )}
        </Card>
      )}

      {(history.data?.length ?? 0) > 0 && (
        <Card padding="0">
          <h2 style={{ margin: 0, padding: "var(--kz-space-4)", fontSize: "var(--kz-font-size-md)" }}>
            取り込みの履歴
          </h2>
          <Table head={["日時", "種類", "ファイル", "状態", "結果"]}>
            {history.data!.map((b) => (
              <tr key={b.id}>
                <Td style={{ whiteSpace: "nowrap" }}>
                  <span className="kz-num">{formatDateTime(b.created_at)}</span>
                </Td>
                <Td>{KINDS.find((k) => k.value === b.kind)?.label ?? b.kind}</Td>
                <Td>{b.file_ref ?? "—"}</Td>
                <Td>
                  {b.status === "applied" ? <Badge tone="ok">反映済み</Badge> : <Badge>未反映</Badge>}
                </Td>
                <Td style={{ fontSize: "var(--kz-font-size-sm)", color: "var(--kz-color-ink-muted)" }}>
                  {b.summary?.applied
                    ? `新規 ${b.summary.applied.created} / 更新 ${b.summary.applied.updated}`
                    : "—"}
                </Td>
              </tr>
            ))}
          </Table>
        </Card>
      )}
    </>
  );
}
